import React, { useState } from 'react';
import { Cliente } from '../../types';
import { useClientes } from '../../context/ClientesContext';
import { X, Building2, User as UserIcon } from 'lucide-react';
import { useCerrarConEscape } from '../../hooks/useCerrarConEscape';

interface Props {
  cliente?: Cliente;
  onClose: () => void;
  onGuardado?: (id: string) => void;
}

const claseInput =
  'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 ' +
  'text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent';

const Campo: React.FC<{ label: string; children: React.ReactNode; ayuda?: string }> =
  ({ label, children, ayuda }) => (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">{label}</label>
      {children}
      {ayuda && <p className="text-[11px] text-gray-500 dark:text-gray-500">{ayuda}</p>}
    </div>
  );

export const ModalCliente: React.FC<Props> = ({ cliente, onClose, onGuardado }) => {
  useCerrarConEscape(onClose);
  const { crearCliente, actualizarCliente } = useClientes();

  const [f, setF] = useState({
    nombre: cliente?.nombre ?? '',
    tipo: cliente?.tipo ?? ('particular' as Cliente['tipo']),
    nif: cliente?.nif ?? '',
    email: cliente?.email ?? '',
    telefono: cliente?.telefono ?? '',
    direccion: cliente?.direccion ?? '',
    codigoPostal: cliente?.codigoPostal ?? '',
    poblacion: cliente?.poblacion ?? '',
    provincia: cliente?.provincia ?? '',
    pais: cliente?.pais ?? 'España',
    notas: cliente?.notas ?? '',
    activo: cliente?.activo ?? true,
  });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const set = (k: keyof typeof f, v: any) => setF(prev => ({ ...prev, [k]: v }));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.nombre.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    setGuardando(true);
    setError('');

    // Los vacíos se guardan como undefined para que no ensucien el documento
    const limpio = (v: string) => (v.trim() ? v.trim() : undefined);
    const datos = {
      nombre: f.nombre.trim(),
      tipo: f.tipo,
      nif: limpio(f.nif),
      email: limpio(f.email),
      telefono: limpio(f.telefono),
      direccion: limpio(f.direccion),
      codigoPostal: limpio(f.codigoPostal),
      poblacion: limpio(f.poblacion),
      provincia: limpio(f.provincia),
      pais: limpio(f.pais),
      notas: limpio(f.notas),
      activo: f.activo,
    };

    try {
      if (cliente) {
        await actualizarCliente(cliente.id, datos);
        onGuardado?.(cliente.id);
      } else {
        const id = await crearCliente(datos);
        onGuardado?.(id);
      }
      onClose();
    } catch (err) {
      console.error(err);
      setError('No se pudo guardar. Inténtalo de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center capa-hoja"
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="hoja bg-white dark:bg-gray-800 w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl
                   max-h-full overflow-y-auto shadow-2xl">

        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700
                        px-5 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {cliente ? 'Editar cliente' : 'Nuevo cliente'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar"
            className="boton-cerrar text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={enviar} className="p-5 space-y-4">

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                            text-red-700 dark:text-red-300 rounded-lg px-3 py-2 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {([
              { v: 'particular', label: 'Particular', Icon: UserIcon },
              { v: 'empresa', label: 'Empresa', Icon: Building2 },
            ] as const).map(o => (
              <button key={o.v} type="button" onClick={() => set('tipo', o.v)}
                aria-pressed={f.tipo === o.v}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-semibold transition ${
                  f.tipo === o.v
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                }`}>
                <o.Icon className="w-4 h-4" /> {o.label}
              </button>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo label={f.tipo === 'empresa' ? 'Razón social *' : 'Nombre y apellidos *'}>
              <input type="text" value={f.nombre} onChange={e => set('nombre', e.target.value)}
                placeholder={f.tipo === 'empresa' ? 'Talleres Marta S.L.' : 'Marta Ruiz'}
                className={claseInput} autoFocus />
            </Campo>
            <Campo label={f.tipo === 'empresa' ? 'CIF' : 'NIF'}
              ayuda="Hará falta el día que emitas facturas.">
              <input type="text" value={f.nif} onChange={e => set('nif', e.target.value)}
                placeholder="B12345678" className={claseInput} />
            </Campo>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo label="Email">
              <input type="email" value={f.email} onChange={e => set('email', e.target.value)}
                placeholder="marta@ejemplo.com" className={claseInput} />
            </Campo>
            <Campo label="Teléfono">
              <input type="tel" value={f.telefono} onChange={e => set('telefono', e.target.value)}
                placeholder="600 000 000" className={claseInput} />
            </Campo>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">
              Dirección
            </p>
            <div className="space-y-3">
              <Campo label="Calle y número">
                <input type="text" value={f.direccion} onChange={e => set('direccion', e.target.value)}
                  placeholder="Calle Mayor, 12, 3ºB" className={claseInput} />
              </Campo>
              <div className="grid gap-3 sm:grid-cols-3">
                <Campo label="Código postal">
                  <input type="text" value={f.codigoPostal} onChange={e => set('codigoPostal', e.target.value)}
                    placeholder="28001" className={claseInput} />
                </Campo>
                <Campo label="Población">
                  <input type="text" value={f.poblacion} onChange={e => set('poblacion', e.target.value)}
                    placeholder="Madrid" className={claseInput} />
                </Campo>
                <Campo label="Provincia">
                  <input type="text" value={f.provincia} onChange={e => set('provincia', e.target.value)}
                    placeholder="Madrid" className={claseInput} />
                </Campo>
              </div>
              <Campo label="País">
                <input type="text" value={f.pais} onChange={e => set('pais', e.target.value)}
                  className={claseInput} />
              </Campo>
            </div>
          </div>

          <Campo label="Notas" ayuda="Preferencias, acuerdos, lo que convenga recordar.">
            <textarea value={f.notas} onChange={e => set('notas', e.target.value)} rows={3}
              placeholder="Prefiere acabado mate. Paga por transferencia."
              className={`${claseInput} resize-y`} />
          </Campo>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={f.activo} onChange={e => set('activo', e.target.checked)}
              className="w-4 h-4 rounded accent-purple-600" />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Cliente activo
              <span className="text-gray-500 dark:text-gray-400">
                {' '}— los inactivos no salen en los desplegables de presupuesto
              </span>
            </span>
          </label>

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={guardando}
              className="flex-1 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white
                         text-sm font-semibold transition disabled:opacity-60">
              {guardando ? 'Guardando…' : cliente ? 'Guardar cambios' : 'Crear cliente'}
            </button>
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 text-sm font-semibold
                         hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
